import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Lifebuoy, CircleNotch, CheckCircle, PlusCircle } from "@phosphor-icons/react";

const CATEGORIES = [
  { v: "general", label: "General question" },
  { v: "quote", label: "Custom project quote" },
  { v: "scheduling", label: "Schedule advisory call" },
  { v: "technical", label: "Technical / portal issue" },
];

const CAT_STYLES = {
  general: "bg-zinc-50 text-zinc-700 border-zinc-200",
  quote: "bg-indigo-50 text-indigo-800 border-indigo-200",
  scheduling: "bg-emerald-50 text-emerald-800 border-emerald-200",
  technical: "bg-amber-50 text-amber-800 border-amber-200",
};

export default function SupportPanel() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", category: "general" });

  const { data: tickets, isLoading } = useQuery({
    queryKey: ["support-tickets"],
    queryFn: () => api.get("/portal/support-tickets").then((r) => r.data),
  });

  const create = useMutation({
    mutationFn: () => api.post("/portal/support-tickets", form),
    onSuccess: () => {
      toast.success("Ticket submitted — we'll respond within one business day.");
      qc.invalidateQueries({ queryKey: ["support-tickets"] });
      setForm({ subject: "", body: "", category: "general" });
      setShowNew(false);
    },
    onError: (e) => toast.error(errText(e)),
  });

  const openTickets = (tickets || []).filter((t) => t.status === "open");
  const resolvedTickets = (tickets || []).filter((t) => t.status === "resolved");

  return (
    <div className="space-y-4" data-testid="support-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
            <Lifebuoy size={18} weight="fill" className="text-indigo-600" />
            Support & Requests
          </h2>
          <p className="text-[11px] text-zinc-500 mt-0.5">Ask a quick question, request a custom project quote, or schedule an advisory call.</p>
        </div>
        <Button size="sm" onClick={() => setShowNew((s) => !s)} data-testid="support-new-btn">
          <PlusCircle size={13} weight="fill" className="mr-1" /> New ticket
        </Button>
      </div>

      {showNew && (
        <div className="rounded-lg border border-zinc-200 bg-white p-4 space-y-2.5" data-testid="support-new-form">
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider text-zinc-500 mb-1">Category</label>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button
                  key={c.v}
                  data-testid={`support-cat-${c.v}`}
                  onClick={() => setForm((s) => ({ ...s, category: c.v }))}
                  className={`text-[11px] font-medium rounded-md border px-2 py-1 transition-colors duration-150 ${form.category === c.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-slate-900"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider text-zinc-500 mb-1">Subject</label>
            <Input
              data-testid="support-subject-input"
              value={form.subject}
              onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))}
              placeholder="One-line summary…"
              maxLength={200}
            />
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider text-zinc-500 mb-1">Details</label>
            <textarea
              data-testid="support-body-input"
              value={form.body}
              onChange={(e) => setForm((s) => ({ ...s, body: e.target.value }))}
              rows={4}
              placeholder="Give us the details — background, timeline, and anything we should know."
              className="w-full text-sm border border-zinc-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-slate-900"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowNew(false)} data-testid="support-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={() => create.mutate()} disabled={create.isPending || !form.subject.trim() || !form.body.trim()} data-testid="support-submit-btn">
              {create.isPending ? <><CircleNotch size={13} className="animate-spin mr-1" /> Sending…</> : "Submit ticket"}
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="py-6 text-center"><CircleNotch size={18} className="animate-spin mx-auto text-zinc-400" /></div>
      ) : (
        <>
          <TicketList tickets={openTickets} label="Open" empty="No open tickets. When you send one, it'll appear here." testid="support-open-list" />
          {resolvedTickets.length > 0 && (
            <TicketList tickets={resolvedTickets} label="Resolved" empty="" testid="support-resolved-list" />
          )}
        </>
      )}
    </div>
  );
}

function TicketList({ tickets, label, empty, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-1.5">{label} ({tickets.length})</p>
      {tickets.length === 0 ? (
        <p className="text-[11.5px] text-zinc-400 italic">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {tickets.map((t) => (
            <li key={t.id} className="rounded-lg border border-zinc-200 bg-white p-3" data-testid={`support-ticket-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[9px] uppercase tracking-wider rounded-sm border px-1 py-0.5 ${CAT_STYLES[t.category] || CAT_STYLES.general}`}>{t.category}</span>
                    {t.status === "resolved" && (
                      <span className="text-[9px] uppercase tracking-wider rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-200 px-1 py-0.5">
                        <CheckCircle size={9} weight="fill" className="inline mr-0.5" /> Resolved
                      </span>
                    )}
                    <p className="text-sm font-semibold text-slate-900">{t.subject}</p>
                  </div>
                  <p className="text-[11.5px] text-zinc-600 mt-1 whitespace-pre-line">{t.body}</p>
                  {t.resolution && (
                    <div className="mt-2 rounded-sm border border-emerald-200 bg-emerald-50/70 px-2 py-1.5">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-800 mb-0.5">CPA response</p>
                      <p className="text-[11.5px] text-emerald-900 whitespace-pre-line">{t.resolution}</p>
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-zinc-400 whitespace-nowrap">{new Date(t.created_at).toLocaleDateString()}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
